import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { TranslateModule } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';

import { InputComponent, SelectSearchComponent } from '@beautyapp/form';
import {
  ServicePackageService,
  CustomerService,
  PackagePaymentMethod,
  Bank,
} from '@beautyapp/core';
import { AddUserComponent } from '@beautyapp/shared';
import { UserViewComponent } from '@beautyapp/users';
import { PaymentMethodBlockComponent } from '../shared/payment-method-block.component';
import { paymentSumMismatch } from '../shared/payment-method-fields';

@Component({
  selector: 'beautyapp-sell-package-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    NzDividerModule,
    TranslateModule,
    FormsModule,
    NzCheckboxModule,
    InputComponent,
    SelectSearchComponent,
    PaymentMethodBlockComponent,
  ],
  templateUrl: './sell-package-modal.component.html',
})
export class SellPackageModalComponent {
  private modalRef = inject(NzModalRef);
  private packageService = inject(ServicePackageService);
  private customerService = inject(CustomerService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  private modalService = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private data: {
    packageId: string;
    packageName: string;
    packagePrice: number;
  } = inject(NZ_MODAL_DATA);

  loading = signal(false);
  customers = signal<Array<{ id: string; name: string; balance: number }>>([]);
  // beaut-251 gift: userId is the payer; when on, a separate beneficiary consumes
  // the package. Independent search pool from the payer's `customers()`.
  isGift = signal(false);
  beneficiaries = signal<Array<{ id: string; name: string }>>([]);
  // Balance of the currently-selected customer, shown under the customer
  // selector so the cashier can see whether BALANCE payment is viable
  // before submitting (avoids hitting backend's insufficient-balance error).
  selectedBalance = signal<number | null>(null);

  form = new FormGroup({
    userId: new FormControl<string | null>(null, Validators.required),
    paymentMethod: new FormControl<PackagePaymentMethod>(
      'CASH',
      Validators.required
    ),
    cash: new FormControl<number | null>(null),
    card: new FormControl<number | null>(null),
    balance: new FormControl<number | null>(null),
    bank: new FormControl<Bank | null>(null),
    bankTransfer: new FormControl<number | null>(null),
    bankTransferBank: new FormControl<Bank | null>(null),
    consignation: new FormControl<number | null>(null),
    comment: new FormControl<string | null>(null),
    beneficiaryUserId: new FormControl<string | null>(null),
  });

  // Cross-field validator: for amount methods, the entered channels must EQUAL
  // the package price (no over/under payment). Shared with the in-booking
  // package-sell-dialog via the paymentSumMismatch helper so the two modals can
  // never drift (the drift was the root cause of beaut-219).
  private readonly paymentSumValidator = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const v = group.getRawValue();
    const mismatch = paymentSumMismatch(v.paymentMethod, this.packagePrice, v);
    return mismatch ? { paymentSumMismatch: mismatch } : null;
  };

  constructor() {
    // Enforce sum-equals-price on every payment/method change.
    this.form.addValidators(this.paymentSumValidator);
    this.form.updateValueAndValidity({ emitEvent: false });
    // Mirror the selected customer's balance into selectedBalance. We can only
    // resolve the balance from the customers() pool the search populated, so
    // if the user picks before searching (impossible via the current UI) or
    // the search result was paged out, this stays null and the UI hides.
    this.form.controls.userId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((userId) => {
        if (!userId) {
          this.selectedBalance.set(null);
          return;
        }
        const match = this.customers().find((c) => c.id === userId);
        this.selectedBalance.set(match ? Number(match.balance ?? 0) : null);
      });
  }

  get packageName(): string {
    return this.data.packageName;
  }

  get packagePrice(): number {
    return this.data.packagePrice;
  }

  onCustomerSearch(search: string): void {
    if (!search || search.length < 2) {
      this.customers.set([]);
      return;
    }
    this.customerService.search(search).subscribe({
      next: (users: any[]) => {
        this.customers.set(
          users.map((u: any) => ({
            id: u.id,
            name: u.fullName + (u.mobilePhone ? ` - ${u.mobilePhone}` : ''),
            balance: Number(u.balance ?? 0),
          }))
        );
      },
    });
  }

  toggleGift(on: boolean): void {
    this.isGift.set(on);
    const c = this.form.controls.beneficiaryUserId;
    c.setValidators(on ? [Validators.required] : []);
    if (!on) c.setValue(null);
    c.updateValueAndValidity();
  }

  // Independent search for the beneficiary (mirrors onCustomerSearch, without
  // balance — the beneficiary never pays).
  onBeneficiarySearch(search: string): void {
    if (!search || search.length < 2) {
      this.beneficiaries.set([]);
      return;
    }
    this.customerService.search(search).subscribe({
      next: (users: any[]) => {
        this.beneficiaries.set(
          users.map((u: any) => ({
            id: u.id,
            name: u.fullName + (u.mobilePhone ? ` - ${u.mobilePhone}` : ''),
          }))
        );
      },
    });
  }

  // Map a raw user (from search or the add-user modal) into a customers() entry.
  private toCustomer(u: any): { id: string; name: string; balance: number } {
    return {
      id: u.id,
      name: u.fullName + (u.mobilePhone ? ` - ${u.mobilePhone}` : ''),
      balance: Number(u.balance ?? 0),
    };
  }

  // Same behaviour as the visit-create flow: open the shared add-user modal and,
  // on success, add the new customer to the pool and select them.
  addNewUser(): void {
    const modal = this.modalService.create({
      nzTitle: this.translate.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
    });
    modal.afterClose.subscribe((res: any) => {
      if (!res?.id) return;
      const created = this.toCustomer(res);
      // Prepend so the new user is in the pool before userId fires (the
      // valueChanges subscriber resolves the balance from customers()).
      this.customers.set([created, ...this.customers()]);
      this.form.patchValue({ userId: created.id });
    });
  }

  // beaut-251 QA: create the beneficiary inline when they're not yet a customer.
  addNewBeneficiary(): void {
    const modal = this.modalService.create({
      nzTitle: this.translate.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
    });
    modal.afterClose.subscribe((res: any) => {
      if (!res?.id) return;
      const created = {
        id: res.id,
        name: res.fullName + (res.mobilePhone ? ` - ${res.mobilePhone}` : ''),
      };
      this.beneficiaries.set([created, ...this.beneficiaries()]);
      this.form.patchValue({ beneficiaryUserId: res.id });
    });
  }

  // Same behaviour as the visit-create flow: open the user-view drawer for the
  // selected customer.
  viewUser(): void {
    const userId = this.form.controls.userId.value;
    if (!userId) return;
    const match = this.customers().find((c) => c.id === userId);
    this.drawerService.create<UserViewComponent, { data: string }, string>({
      nzTitle: match?.name ?? '',
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      nzData: { data: userId },
    });
  }

  cancel(): void {
    this.modalRef.close();
  }

  private buildPayload(): Record<string, unknown> | null {
    const value = this.form.getRawValue();
    const {
      userId,
      paymentMethod,
      cash,
      card,
      bank,
      balance,
      bankTransfer,
      bankTransferBank,
      consignation,
      comment,
    } = value;
    if (!userId || !paymentMethod) return null;

    const base: Record<string, unknown> = { userId, paymentMethod };
    if (comment) base['comment'] = comment;
    // beaut-251: buying for another customer.
    const beneficiaryUserId = this.form.controls.beneficiaryUserId.value;
    if (beneficiaryUserId) base['beneficiaryUserId'] = beneficiaryUserId;

    switch (paymentMethod) {
      case 'CASH':
      case 'BARTER':
      case 'BALANCE':
        return base;
      case 'CARD':
        return { ...base, card: card ?? 0, ...(bank ? { bank } : {}) };
      case 'BANK_TRANSFER':
        return {
          ...base,
          bankTransfer: bankTransfer ?? 0,
          ...(bankTransferBank ? { bankTransferBank } : {}),
        };
      case 'MIXED':
        return {
          ...base,
          cash: cash ?? 0,
          card: card ?? 0,
          balance: balance ?? 0,
          bankTransfer: bankTransfer ?? 0,
          ...(bank ? { bank } : {}),
          ...(bankTransferBank ? { bankTransferBank } : {}),
        };
      case 'CONSIGNATION':
        return {
          ...base,
          cash: cash ?? 0,
          card: card ?? 0,
          consignation: consignation ?? 0,
          ...(bank ? { bank } : {}),
        };
      default:
        return base;
    }
  }

  submit(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      return;
    }

    const payload = this.buildPayload();
    if (!payload) return;

    this.loading.set(true);
    this.packageService.sell(this.data.packageId, payload as any).subscribe({
      next: (purchase) => {
        this.loading.set(false);
        this.modalRef.close({ success: true, purchase });
      },
      error: (err) => {
        this.loading.set(false);
        this.message.error(
          err.error?.message ||
            this.translate.instant('servicePackages.messages.sellError')
        );
      },
    });
  }
}
